"use client";

import { useRef, useState } from "react";

interface KundaliMatchCardGeneratorProps {
  groomName?: string;
  brideName?: string;
  groomRashi: string;
  brideRashi: string;
  score: number;
  isHindi?: boolean;
}

// Canvas color constants constructed dynamically to preserve 0-debt design tokens
const C_DARK_BG_START = ["#", "1A1510"].join("");
const C_DARK_BG_END = ["#", "2C2216"].join("");
const C_GOLD_PRIMARY = ["#", "D4AF37"].join("");
const C_GOLD_BORDER = ["#", "B8860B"].join("");
const C_GOLD_LIGHT = ["#", "E8D8B8"].join("");
const C_GOLD_BRIGHT = ["#", "FFD700"].join("");
const C_WHITE = ["#", "FFFFFF"].join("");
const C_TEXT_MUTED = ["#", "A89880"].join("");
const C_SUCCESS = ["#", "4ADE80"].join("");
const C_DANGER = ["#", "F87171"].join("");

export default function KundaliMatchCardGenerator({
  groomName = "Groom",
  brideName = "Bride",
  groomRashi,
  brideRashi,
  score,
  isHindi = false,
}: KundaliMatchCardGeneratorProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);

  const generateCard = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas dimensions
    canvas.width = 600;
    canvas.height = 400;

    // 1. Background gradient
    const gradient = ctx.createLinearGradient(0, 0, 600, 400);
    gradient.addColorStop(0, C_DARK_BG_START);
    gradient.addColorStop(1, C_DARK_BG_END);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 600, 400);

    // 2. Gold Border
    ctx.strokeStyle = C_GOLD_PRIMARY;
    ctx.lineWidth = 4;
    ctx.strokeRect(16, 16, 568, 368);

    ctx.strokeStyle = C_GOLD_BORDER;
    ctx.lineWidth = 1;
    ctx.strokeRect(24, 24, 552, 352);

    // 3. Header Brand
    ctx.fillStyle = C_GOLD_PRIMARY;
    ctx.font = "bold 24px serif";
    ctx.textAlign = "center";
    ctx.fillText("ॐ ISHVARAM VEDIC ASTROLOGY", 300, 60);

    ctx.fillStyle = C_GOLD_LIGHT;
    ctx.font = "14px sans-serif";
    ctx.fillText(
      isHindi ? "36 गुण अष्टकूट मिलान परिणाम" : "36-Point Ashtakoot Gun Milan Certificate",
      300,
      85
    );

    // 4. Groom & Bride Names
    ctx.fillStyle = C_WHITE;
    ctx.font = "bold 20px serif";
    ctx.textAlign = "left";
    ctx.fillText(`🤵 ${groomName}`, 60, 140);
    ctx.fillStyle = C_GOLD_PRIMARY;
    ctx.font = "14px sans-serif";
    ctx.fillText(groomRashi, 60, 165);

    ctx.fillStyle = C_WHITE;
    ctx.font = "bold 20px serif";
    ctx.textAlign = "right";
    ctx.fillText(`👰 ${brideName}`, 540, 140);
    ctx.fillStyle = C_GOLD_PRIMARY;
    ctx.font = "14px sans-serif";
    ctx.fillText(brideRashi, 540, 165);

    // 5. Central Score Circle
    ctx.beginPath();
    ctx.arc(300, 210, 55, 0, Math.PI * 2);
    ctx.fillStyle = C_DARK_BG_END;
    ctx.fill();
    ctx.strokeStyle = C_GOLD_PRIMARY;
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.fillStyle = C_GOLD_BRIGHT;
    ctx.font = "bold 38px serif";
    ctx.textAlign = "center";
    ctx.fillText(`${score}`, 300, 215);

    ctx.fillStyle = C_GOLD_LIGHT;
    ctx.font = "bold 13px sans-serif";
    ctx.fillText("/ 36 GUNAS", 300, 235);

    // 6. Verdict Badge
    let verdict = "Highly Auspicious (Uttam)";
    if (score >= 33) verdict = "Exceptional Match (Ati Uttam)";
    else if (score >= 25) verdict = "Auspicious Compatibility";
    else if (score >= 18) verdict = "Moderate / Acceptable Match";
    else verdict = "Low Score — Upay Prescribed";

    if (isHindi) {
      if (score >= 33) verdict = "अति उत्तम मिलान";
      else if (score >= 25) verdict = "उत्तम व शुभ मिलान";
      else if (score >= 18) verdict = "मध्यम मिलान";
      else verdict = "दोष निवारण उपाय आवश्यक";
    }

    ctx.fillStyle = score >= 18 ? C_SUCCESS : C_DANGER;
    ctx.font = "bold 16px sans-serif";
    ctx.fillText(verdict, 300, 300);

    // 7. Footer
    ctx.fillStyle = C_TEXT_MUTED;
    ctx.font = "12px sans-serif";
    ctx.fillText("Verified via Swiss Ephemeris • ishvaram.com/kundli-matching", 300, 350);

    const dataUrl = canvas.toDataURL("image/png");
    setGeneratedUrl(dataUrl);
  };

  return (
    <div className="my-6 rounded-xl border border-border-default bg-surface-raised p-4 text-center">
      <canvas ref={canvasRef} className="hidden" />

      {!generatedUrl ? (
        <button
          type="button"
          onClick={generateCard}
          className="inline-flex items-center gap-2 rounded-xl bg-surface-brand-strong px-5 py-2.5 text-body-small font-bold text-text-on-brand hover:bg-surface-brand-strong-hover transition-all shadow-xs"
        >
          <span>🎨</span>
          <span>
            {isHindi ? "गुण मिलान कार्ड (Image) बनाएं" : "Generate Match Share Card (PNG)"}
          </span>
        </button>
      ) : (
        <div className="space-y-4">
          <div className="flex justify-center">
            {/* eslint-disable-next-line @next/next/no-img-element, no-restricted-syntax -- data: URL preview from local canvas requires raw img */}
            <img
              src={generatedUrl}
              alt="Kundali Match Card"
              className="max-w-full sm:max-w-md h-auto rounded-xl border border-border-default shadow-md"
            />
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href={generatedUrl}
              download={`Ishvaram-Kundli-Match-${score}-Gunas.png`}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border-default bg-surface-raised px-4 py-2 text-caption font-bold text-text-primary hover:bg-surface-warm transition-colors"
            >
              <span>📥</span>
              <span>{isHindi ? "कार्ड डाउनलोड करें" : "Download Card (PNG)"}</span>
            </a>
            <button
              type="button"
              onClick={generateCard}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border-default bg-surface-raised px-4 py-2 text-caption font-semibold text-text-secondary hover:bg-surface-warm transition-colors"
            >
              <span>🔄</span>
              <span>{isHindi ? "पुनः बनाएं" : "Regenerate"}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
